// prop-types is a library for typechecking of props
import React from 'react'
import PropTypes from 'prop-types'

// Slick Slider
import Slider from 'react-slick'

// @mui material components
import { Grid, Box, Typography, IconButton } from '@mui/material'

import parcoSwoosh from 'assets/images/parcoSwoosh.png'
import {
  FormatQuote,
  ArrowBackIosNew,
  ArrowForwardIos,
} from '@mui/icons-material'

const NextArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      right: { xs: '8px', md: '-20px' },
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2370ED',
      backgroundColor: 'rgba(255,255,255,0.8)',
      boxShadow: 1,
      '&:hover': { backgroundColor: '#2370ED', color: '#fff' },
    }}
  >
    <ArrowForwardIos fontSize="small" />
  </IconButton>
)

const PrevArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      left: { xs: '8px', md: '-20px' },
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2370ED',
      backgroundColor: 'rgba(255,255,255,0.8)',
      boxShadow: 1,
      '&:hover': { backgroundColor: '#2370ED', color: '#fff' },
    }}
  >
    <ArrowBackIosNew fontSize="small" />
  </IconButton>
)

function Testimonials({ testimonials }) {
  const settings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000,
    speed: 600,
    cssEase: 'ease-in-out',
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    pauseOnHover: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  }

  return (
    <Box
      position="relative"
      bottom="0"
      width="100%"
      p={2}
      sx={{
        backgroundColor: '#A7C6F8',
        backgroundImage: `url(${parcoSwoosh})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 700,
          mx: 'auto',
          textAlign: 'center',
          py: 6,
          pt: { xs: 3, md: 4 },
          '& .slick-dots': {
            marginBottom: '-4px',
          },
          '& .slick-dots li button:before': {
            fontSize: '12px',
          },
          '& .slick-dots li.slick-active button:before': {
            color: '#2370ED',
          },
        }}
      >
        <Slider {...settings}>
          {testimonials.map((item, index) => (
            <Box key={index} sx={{ px: { xs: 8, md: 6 } }}>
              <Box
                sx={{
                  display: 'flex',
                  width: '40px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: 'auto',
                  backgroundColor: 'rgba(35,112,237,0.15)',
                  borderRadius: 100,
                  padding: 1,
                  mb: 1,
                }}
              >
                <FormatQuote
                  sx={{
                    fontSize: '24px !important',
                    color: '#2370ED',
                  }}
                />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '20px', md: '30px' },
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {item.text}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ color: '#2370ED', fontStyle: 'italic' }}
              >
                {item.author}
              </Typography>
            </Box>
          ))}
        </Slider>
      </Box>
    </Box>
  )
}

// Setting default values for the props of Testimonials
Testimonials.defaultProps = {
  testimonials: [
    {
      text: "PARCO took the time to get me on track—I recommend them wholeheartedly. It's never too late to start planning.",
      author: 'Shirletta M @ DoD',
    },
    {
      text: 'After 42 years at the DoD, retirement felt overwhelming—PARCO explained everything clearly and put me at ease. I finally have peace of mind about retiring.',
      author: 'Ana R @ DoD',
    },
    {
      text: 'PARCO gave me the best retirement advice and made the whole process so much less stressful. They were always quick to respond when I had questions.',
      author: 'Tammie S @ FBI',
    },
    {
      text: 'PARCO explained everything in terms I could understand. 10/10 would recommend chatting with them to set your mind at ease.',
      author: 'Kathleen S @ NIH',
    },
    {
      text: "I was scared for retirement, but now I'm actually excited. PARCO explained my benefits with patience and made it feel simple.",
      author: 'Dixie T @ USPS',
    },
    {
      text: 'They explained strategies and helped me make a plan. After a couple conversations with PARCO, I finally feel confident about retirement.',
      author: 'Doug L @ USPS',
    },
  ],
}

// Typechecking props for the Testimonials
Testimonials.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      author: PropTypes.string.isRequired,
    })
  ),
}

export default Testimonials
